/**
 * PATH: apps/hr/src/pages/clock/ClockSubcomponents.tsx
 * FILE_ID: HR-TIMECLOCK-SUBC-001
 * PURPOSE: Presentational subcomponents for the time-clock page.
 * WHY: Keeps ClockPage.tsx as a thin orchestrator; each
 *      subcomponent has a single render concern. Ported from
 *      apps/workforce/src/pages/clock/ClockSubcomponents.tsx.
 */

import { Clock, MapPin, Briefcase, AlertTriangle, CheckCircle2, Building2, AlertCircle } from 'lucide-react'
import { formatDuration, formatPunchTime } from './clockHelpers'
import type {
  CurrentSessionResponse,
  GeofenceStatus,
  JobOption,
  SiteOption,
} from './clockTypes'

export function FeatureDisabledNotice() {
  return (
    <div className="max-w-xl mx-auto mt-16 p-8 rounded-lg border border-slate-200 bg-slate-50 text-center">
      <Clock className="w-10 h-10 mx-auto text-slate-400 mb-3" />
      <h2 className="text-lg font-semibold text-slate-700">
        Time clock is not enabled for your organisation
      </h2>
      <p className="mt-2 text-sm text-slate-500">
        Ask your admin to turn on the{' '}
        <code className="px-1 bg-slate-200 rounded">Time clock</code> feature in HR settings.
      </p>
    </div>
  )
}

export function StatusBanner({
  error, success,
}: {
  error: string | null
  success: string | null
}) {
  if (error) {
    return (
      <div className="mt-4 p-3 rounded border border-red-300 bg-red-50 text-sm text-red-700 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    )
  }
  if (success) {
    return (
      <div className="mt-4 p-3 rounded border border-green-300 bg-green-50 text-sm text-green-700 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{success}</span>
      </div>
    )
  }
  return null
}

export function SessionDetails({
  session, onClockOut, busy,
}: {
  session: CurrentSessionResponse
  onClockOut: () => void
  busy: boolean
}) {
  return (
    <div className="mt-4 space-y-3 text-sm text-slate-700">
      <div className="flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-slate-400" />
        <span>{session.job_number ? `Job ${session.job_number}` : 'Active session'}</span>
      </div>
      <div>
        <span className="text-slate-500">Started:</span>{' '}
        {formatPunchTime(session.punch_in_at)}{' '}
        ({formatDuration(session.duration_so_far_minutes)} so far)
      </div>
      {session.gps_location && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <MapPin className="w-3 h-3" />
          {session.gps_location.latitude.toFixed(4)}, {session.gps_location.longitude.toFixed(4)}
        </div>
      )}
      <button
        type="button"
        onClick={onClockOut}
        disabled={busy}
        className="mt-3 w-full px-4 py-2 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium"
      >
        {busy ? 'Clocking out…' : 'Clock out'}
      </button>
    </div>
  )
}

export function ClockInForm({
  jobs, selectedJobId, onSelect,
  sites, selectedSiteId, onSelectSite,
  onClockIn, busy,
}: {
  jobs: JobOption[]
  selectedJobId: number | null
  onSelect: (id: number | null) => void
  sites: SiteOption[]
  selectedSiteId: number | null
  onSelectSite: (id: number | null) => void
  onClockIn: () => void
  busy: boolean
}) {
  const showSites = sites.length > 0
  return (
    <div className="mt-4 space-y-3">
      <label className="block text-sm font-medium text-slate-700">
        Job
        <select
          value={selectedJobId ?? ''}
          onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          disabled={busy}
        >
          <option value="">— pick a job —</option>
          {jobs.map((j) => (
            <option key={j.quotech_job_id} value={j.quotech_job_id}>
              #{j.quotech_job_id} {j.name}
              {j.customer_name ? ` — ${j.customer_name}` : ''}
            </option>
          ))}
        </select>
      </label>
      {jobs.length === 0 && (
        <p className="text-xs text-slate-500">
          No active jobs found. Ask your supervisor to assign you to a job before clocking in.
        </p>
      )}
      {showSites && (
        <label className="block text-sm font-medium text-slate-700">
          <span className="flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-slate-400" />
            Site (optional)
          </span>
          <select
            value={selectedSiteId ?? ''}
            onChange={(e) => onSelectSite(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            disabled={busy}
          >
            <option value="">— no specific site —</option>
            {sites.map((s) => (
              s.quotech_site_id !== null ? (
                <option key={s.quotech_site_id} value={s.quotech_site_id}>
                  {s.name}
                  {s.has_geofence ? ` (geofence: ${s.geofence_radius_m}m)` : ''}
                </option>
              ) : null
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-400">
            Picking a site checks your GPS against the site&apos;s geofence — out-of-range punches still go through but get flagged for supervisor review.
          </p>
        </label>
      )}
      <button
        type="button"
        onClick={onClockIn}
        disabled={busy || !selectedJobId}
        className="w-full px-4 py-2 rounded bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-medium"
      >
        {busy ? 'Clocking in…' : 'Clock in'}
      </button>
      <p className="text-xs text-slate-400">
        We&apos;ll capture your location if your device allows it. You can clock out from this page anytime.
      </p>
    </div>
  )
}

/**
 * GeofenceVerdictBanner — coloured pill that summarises the
 * geofence outcome of the most recent successful clock-in.
 *
 * Three tones, none of them block the worker (the BE accepts all
 * verdicts; the supervisor approval queue handles outliers):
 *   green  — in_geofence
 *   amber  — out_geofence (distance + radius shown for context)
 *   red    — no_gps
 *   slate  — no_site / no_site_geofence (informational, no flag)
 */
export function GeofenceVerdictBanner({
  status,
  distanceMeters,
  radiusMeters,
}: {
  status: GeofenceStatus
  distanceMeters: number | null
  radiusMeters: number | null
}) {
  if (status === 'no_site' || status === 'no_site_geofence') {
    return null
  }

  const palettes: Record<GeofenceStatus, { wrap: string; icon: JSX.Element; label: string; detail: string }> = {
    in_geofence: {
      wrap: 'border-green-300 bg-green-50 text-green-700',
      icon: <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />,
      label: 'On site',
      detail: distanceMeters !== null
        ? `${Math.round(distanceMeters)} m from the site centre.`
        : 'Inside the site geofence.',
    },
    out_geofence: {
      wrap: 'border-amber-300 bg-amber-50 text-amber-700',
      icon: <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />,
      label: 'Outside the geofence',
      detail: distanceMeters !== null && radiusMeters !== null
        ? `Punched ${Math.round(distanceMeters)} m from the site (geofence radius ${radiusMeters} m). Your supervisor will be asked to confirm.`
        : 'Your supervisor will be asked to confirm this punch.',
    },
    no_gps: {
      wrap: 'border-red-300 bg-red-50 text-red-700',
      icon: <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />,
      label: 'No location captured',
      detail: 'Enable location services so we can match your punch to a site. Your supervisor will be asked to confirm this entry.',
    },
    // Should never be rendered — guarded above; included so the
    // exhaustive `Record` keeps TypeScript happy.
    no_site: { wrap: '', icon: <></>, label: '', detail: '' },
    no_site_geofence: { wrap: '', icon: <></>, label: '', detail: '' },
  }

  const palette = palettes[status]
  return (
    <div className={`mt-3 p-3 rounded border text-sm flex items-start gap-2 ${palette.wrap}`}>
      {palette.icon}
      <div>
        <div className="font-medium">{palette.label}</div>
        <div className="text-xs mt-0.5 opacity-90">{palette.detail}</div>
      </div>
    </div>
  )
}
